import { ttsApi } from '../api';
import { AudioSegment, ParallelTTSState, SpeechEventCallback } from './types';
import { AudioPlayer } from './audioPlayer';

/**
 * 並列TTS処理を管理するクラス
 */
export class ParallelTTSProcessor {
  private state: ParallelTTSState;
  private audioPlayer: AudioPlayer;
  private onEvent?: SpeechEventCallback;
  private segmentTransitionDelay: number;
  private speakingRate: number;

  constructor(
    audioPlayer: AudioPlayer,
    onEvent?: SpeechEventCallback,
    segmentTransitionDelay: number = 100,
    speakingRate: number = 1.0
  ) {
    this.audioPlayer = audioPlayer;
    this.onEvent = onEvent;
    this.segmentTransitionDelay = segmentTransitionDelay;
    this.speakingRate = speakingRate;
    this.state = {
      isActive: false,
      segments: [],
      currentIndex: 0,
      cache: new Map(),
    };
  }

  /**
   * 音声速度を更新
   */
  setSpeakingRate(rate: number): void {
    this.speakingRate = rate;
  }

  /**
   * 並列TTS処理を開始
   */
  async process(textSegments: string[]): Promise<void> {
    try {
      this.state.isActive = true;
      this.state.currentIndex = 0;
      this.state.cache.clear();
      this.state.segments = this.initializeSegments(textSegments);

      // 全セグメントを並列でTTS処理
      const ttsPromises = textSegments.map((segment, index) =>
        this.generateSegmentAudio(segment, index)
      );

      // 最初のセグメントの完了を待って再生開始
      await ttsPromises[0];

      if (this.state.isActive) {
        this.onEvent?.({ type: 'speaking_started' });
        await this.playSegmentsSequentially(ttsPromises);
      }
    } catch (error) {
      console.error('❌ 並列TTS処理エラー:', error);
      this.cleanup();
      this.onEvent?.({
        type: 'error',
        error: error instanceof Error ? error.message : 'TTS処理エラー',
      });
    }
  }

  /**
   * セグメント配列を初期化
   */
  private initializeSegments(textSegments: string[]): AudioSegment[] {
    return textSegments.map((text, index) => ({
      index,
      text,
      audioBlob: null,
      status: 'pending',
    }));
  }

  // TTS生成の最大試行回数（1回目失敗 + リトライ）
  private static readonly TTS_MAX_ATTEMPTS = 3;
  // リトライ間の基本待機時間(ms)。試行ごとに線形に増やす。
  private static readonly TTS_RETRY_BASE_DELAY = 150;

  /**
   * TTS生成をリトライ付きで実行する。
   *
   * 全セグメントを一斉に並列発火するため、後続セグメントが一時的な負荷や
   * レート制限で失敗しやすい。失敗を握りつぶして無言スキップすると
   * 「先頭の文だけ読まれ、後続の文が読み上げられない」現象になるため、
   * ここで数回リトライして一時障害を吸収する。
   */
  private async synthesizeWithRetry(segment: string): Promise<Blob> {
    let lastError: unknown;
    for (let attempt = 1; attempt <= ParallelTTSProcessor.TTS_MAX_ATTEMPTS; attempt++) {
      try {
        return await ttsApi.synthesizeSpeech(segment, undefined, this.speakingRate);
      } catch (error) {
        lastError = error;
        if (attempt < ParallelTTSProcessor.TTS_MAX_ATTEMPTS) {
          await new Promise((resolve) =>
            setTimeout(resolve, ParallelTTSProcessor.TTS_RETRY_BASE_DELAY * attempt)
          );
        }
      }
    }
    throw lastError;
  }

  /**
   * セグメントの音声を生成
   */
  private async generateSegmentAudio(segment: string, index: number): Promise<void> {
    try {
      this.updateSegmentStatus(index, 'generating');

      const audioBlob = await this.synthesizeWithRetry(segment);

      // キャッシュに保存
      this.state.cache.set(`segment_${index}`, audioBlob);
      this.updateSegmentAudio(index, audioBlob);
      this.updateSegmentStatus(index, 'ready');
    } catch (error) {
      console.error(`❌ セグメント${index + 1}の音声生成エラー:`, error);
      this.updateSegmentStatus(index, 'error');
    }
  }

  /**
   * セグメントを順次再生
   */
  private async playSegmentsSequentially(ttsPromises: Promise<void>[]): Promise<void> {
    for (let i = 0; i < this.state.segments.length; i++) {
      try {
        // 並列モードがキャンセルされた場合は中断
        if (!this.state.isActive) {
          break;
        }

        // 該当セグメントの音声生成完了を待機
        await ttsPromises[i];

        let audioBlob = this.state.cache.get(`segment_${i}`);
        if (!audioBlob) {
          // 並列生成で失敗したセグメント。無言でスキップすると文章が読まれなく
          // なるため、スキップ前にこの場で直列に生成し直す（最後の砦）。
          try {
            audioBlob = await this.synthesizeWithRetry(this.state.segments[i].text);
            this.state.cache.set(`segment_${i}`, audioBlob);
            this.updateSegmentAudio(i, audioBlob);
          } catch (error) {
            console.warn(`⚠️ セグメント${i + 1}の音声生成に最終的に失敗、スキップ`, error);
            continue;
          }
        }

        // 音声再生（キャンセルチェック付き）
        if (this.state.isActive) {
          this.updateSegmentStatus(i, 'playing');
          await this.audioPlayer.play(audioBlob);
          this.updateSegmentStatus(i, 'completed');

          this.onEvent?.({ type: 'segment_completed', index: i });
        }

        // 短い間隔で次のセグメントへ
        if (i < this.state.segments.length - 1 && this.state.isActive) {
          await new Promise((resolve) => setTimeout(resolve, this.segmentTransitionDelay));
        }
      } catch (error) {
        console.error(`❌ セグメント${i + 1}の再生エラー:`, error);
        this.updateSegmentStatus(i, 'error');
      }
    }

    this.cleanup();
  }

  /**
   * セグメントの状態を更新
   */
  private updateSegmentStatus(index: number, status: AudioSegment['status']): void {
    if (this.state.segments[index]) {
      this.state.segments[index].status = status;
    }
  }

  /**
   * セグメントの音声データを更新
   */
  private updateSegmentAudio(index: number, audioBlob: Blob): void {
    if (this.state.segments[index]) {
      this.state.segments[index].audioBlob = audioBlob;
    }
  }

  /**
   * 処理を停止
   */
  stop(): void {
    this.cleanup();
    this.audioPlayer.stop();
  }

  /**
   * クリーンアップ
   */
  private cleanup(): void {
    this.state.isActive = false;
    this.state.cache.clear();
    this.state.segments = [];
    this.state.currentIndex = 0;
    this.onEvent?.({ type: 'speaking_ended' });
  }

  /**
   * 現在の状態を取得
   */
  get isActive(): boolean {
    return this.state.isActive;
  }

  /**
   * 現在の処理状況を取得
   */
  getProgress(): { completed: number; total: number; currentIndex: number } {
    const completed = this.state.segments.filter((s) => s.status === 'completed').length;
    return {
      completed,
      total: this.state.segments.length,
      currentIndex: this.state.currentIndex,
    };
  }
}
